/**
 * SLA / OLA status cards for the right rail — modelled after the KC2
 * SlaOlaStatus component. Shows accordion sections for SLA and OLA,
 * each containing detailed timer cards with status badges, progress bars,
 * elapsed/target durations, and start/due/breach/met timestamps.
 * All corners ≤3px.
 */
import { useEffect, useState, type JSX, type ReactNode } from 'react'
import {
  Clock,
  Gauge,
  Timer as TimerIcon,
  ChevronDown,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Calendar,
  Flag,
  Check,
  X,
  Network,
} from 'lucide-react'
import { formatDateTime } from '../../../utils/timezone.js'
import { computeSlaTimer, slaProgress, humanizeDuration, type SlaTimer } from './sla.js'
import { R, Section } from './primitives.js'

interface SlaConversation {
  firstResponseDueAt: string | null
  resolutionDueAt: string | null
  firstRespondedAt: string | null
  resolvedAt: string | null
  olaDueAt?: string | null
  olaBreached?: boolean
  slaBreached?: boolean
  openedAt?: string | null
  slaStartedAt?: string | null
  slaPolicyName?: string | null
  olaPolicyName?: string | null
  slaFirstResponseMinutes?: number | null
  slaResolutionMinutes?: number | null
  olaResponseTargetMinutes?: number | null
}

const STATUS_ICON: Record<string, JSX.Element> = {
  MET: <CheckCircle2 className="h-3.5 w-3.5" />,
  BREACHED: <XCircle className="h-3.5 w-3.5" />,
  RUNNING: <TimerIcon className="h-3.5 w-3.5" />,
  APPROACHING: <AlertTriangle className="h-3.5 w-3.5" />,
  NONE: <Clock className="h-3.5 w-3.5" />,
}

function TimerCard({
  label,
  dueAt,
  startedAt,
  completedAt,
  targetMinutes,
  timer,
}: {
  label: string
  dueAt: string | null
  startedAt: string | null
  completedAt: string | null
  targetMinutes: number | null
  timer: SlaTimer
}): JSX.Element {
  const pct = Math.round(slaProgress(timer) * 100)
  const isResolution = label.toLowerCase().includes('resolution')
  const isOla = label.toLowerCase().includes('ola')

  return (
    <div className={`${R} border-l-4 ${timer.tone.border} border-y border-r border-slate-200 bg-white p-3 shadow-sm transition-all hover:shadow-md`}>
      {/* Header: label + status badge */}
      <div className="mb-2 flex items-center justify-between border-b border-slate-100 pb-1.5">
        <span className="flex items-center gap-1.5 text-xs font-semibold text-slate-700">
          {isResolution ? <Gauge className="h-3.5 w-3.5" /> : isOla ? <Network className="h-3.5 w-3.5" /> : <TimerIcon className="h-3.5 w-3.5" />}
          {label}
        </span>
        <span
          className={`inline-flex items-center gap-1 ${R} ${timer.tone.bg} px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${timer.tone.text}`}
        >
          {timer.state === 'running' && (
            <span className={`h-1.5 w-1.5 rounded-full ${timer.tone.dot} animate-pulse`} />
          )}
          {timer.state !== 'running' && (STATUS_ICON[timer.statusLabel] ?? <Clock className="h-3 w-3" />)}
          {timer.statusLabel}
        </span>
      </div>

      {/* Main stat: remaining / overdue / met */}
      <div className="mb-2 flex items-center justify-between">
        {timer.state === 'met' ? (
          <div className="flex items-center gap-1.5 text-xs">
            <Check className="h-3.5 w-3.5 text-emerald-600" />
            <span className="text-slate-500">Met at:</span>
            <span className="font-semibold text-slate-700">
              {completedAt ? formatDateTime(completedAt) : '—'}
            </span>
          </div>
        ) : timer.state === 'breached' ? (
          <div className="flex items-center gap-1.5 text-xs">
            <X className="h-3.5 w-3.5 text-red-600" />
            <span className="text-slate-500">Overdue by:</span>
            <span className="font-bold text-red-600">
              {humanizeDuration(Math.abs(timer.deltaMs ?? 0))}
            </span>
          </div>
        ) : (
          <div className="flex items-center gap-1.5 text-xs">
            <TimerIcon className={`h-3.5 w-3.5 ${timer.tone.text}`} />
            <span className="text-slate-500">Time remaining:</span>
            <span className={`font-bold ${timer.tone.text}`}>
              {timer.label}
            </span>
          </div>
        )}
      </div>

      {/* Progress bar */}
      <div className="mb-1.5 h-2 w-full overflow-hidden rounded-full bg-slate-100">
        <div
          className={`h-full ${timer.tone.bar} transition-all duration-1000 ease-linear`}
          style={{ width: `${pct}%` }}
        />
      </div>

      {/* Progress labels: elapsed / target */}
      <div className="mb-2 flex items-center justify-between text-[10px] font-medium text-slate-400">
        <span>{humanizeDuration(timer.elapsedMs)} elapsed</span>
        <span>Target: {targetMinutes ? `${targetMinutes}m` : humanizeDuration(timer.budgetMs)}</span>
      </div>

      {/* Detail rows: started, due, breached, met */}
      <div className="space-y-1 border-t border-slate-100 pt-1.5">
        <div className="flex items-center gap-1.5 text-[11px] text-slate-500">
          <Calendar className="h-3 w-3 text-slate-400" />
          <span>Started: {startedAt ? formatDateTime(startedAt) : '—'}</span>
        </div>
        <div className="flex items-center gap-1.5 text-[11px] text-slate-500">
          <Flag className="h-3 w-3 text-slate-400" />
          <span>Due: {dueAt ? formatDateTime(dueAt) : 'No target set'}</span>
        </div>
        {timer.state === 'breached' && (
          <div className="flex items-center gap-1.5 text-[11px] font-medium text-red-600">
            <XCircle className="h-3 w-3 text-red-500" />
            <span>Breached: {formatDateTime(new Date().toISOString())}</span>
          </div>
        )}
        {timer.state === 'met' && completedAt && (
          <div className="flex items-center gap-1.5 text-[11px] font-medium text-emerald-600">
            <CheckCircle2 className="h-3 w-3 text-emerald-500" />
            <span>Met: {formatDateTime(completedAt)}</span>
          </div>
        )}
      </div>
    </div>
  )
}

function AccordionSection({
  title,
  icon,
  ruleName,
  count,
  defaultOpen = true,
  children,
}: {
  title: string
  icon: JSX.Element
  ruleName?: string | null | undefined
  count: number
  defaultOpen?: boolean
  children: ReactNode
}): JSX.Element {
  const [open, setOpen] = useState(defaultOpen)
  if (count === 0) return <></>
  return (
    <div className={`${R} border border-slate-200 bg-white shadow-sm`}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between px-3 py-2 text-left transition-colors hover:bg-slate-50"
      >
        <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-600">
          {icon}
          {title}
          {ruleName && (
            <span className="ml-1 font-normal normal-case text-slate-400">({ruleName})</span>
          )}
          <span className={`ml-1 ${R} bg-blue-50 px-1.5 py-0.5 text-[10px] font-bold text-blue-600`}>
            {count}
          </span>
        </span>
        <ChevronDown
          className={`h-4 w-4 text-slate-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div className="space-y-2 border-t border-slate-100 p-2.5">{children}</div>
      )}
    </div>
  )
}

export default function SlaOlaPanel({ conversation }: { conversation: SlaConversation }): JSX.Element {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [])

  const startedAt = conversation.slaStartedAt ?? conversation.openedAt ?? null

  const response = computeSlaTimer(
    conversation.firstResponseDueAt,
    conversation.firstRespondedAt,
    now,
    startedAt,
    conversation.slaFirstResponseMinutes ?? null,
  )
  const resolution = computeSlaTimer(
    conversation.resolutionDueAt,
    conversation.resolvedAt,
    now,
    startedAt,
    conversation.slaResolutionMinutes ?? null,
  )
  const ola = computeSlaTimer(
    conversation.olaDueAt ?? null,
    conversation.firstRespondedAt ?? null,
    now,
    startedAt,
    conversation.olaResponseTargetMinutes ?? null,
    true,
  )

  const slaTimers: Array<{ label: string; dueAt: string | null; completedAt: string | null; targetMinutes: number | null; timer: SlaTimer }> = [
    { label: 'Response', dueAt: conversation.firstResponseDueAt, completedAt: conversation.firstRespondedAt, targetMinutes: conversation.slaFirstResponseMinutes ?? null, timer: response },
    { label: 'Resolution', dueAt: conversation.resolutionDueAt, completedAt: conversation.resolvedAt, targetMinutes: conversation.slaResolutionMinutes ?? null, timer: resolution },
  ]
  const olaTimers: Array<{ label: string; dueAt: string | null; completedAt: string | null; targetMinutes: number | null; timer: SlaTimer }> = []
  if (conversation.olaDueAt) {
    olaTimers.push({ label: 'OLA Response', dueAt: conversation.olaDueAt, completedAt: conversation.firstRespondedAt ?? null, targetMinutes: conversation.olaResponseTargetMinutes ?? null, timer: ola })
  }

  const slaCount = slaTimers.filter((t) => t.dueAt).length
  const olaCount = olaTimers.length
  const hasAny = slaCount > 0 || olaCount > 0

  if (!hasAny) {
    return (
      <Section title="SLA / OLA Status">
        <div className="flex items-center gap-2 py-3 text-xs text-slate-400">
          <Clock className="h-4 w-4" />
          <span>No SLA/OLA timers configured for this conversation</span>
        </div>
      </Section>
    )
  }

  return (
    <Section title="SLA / OLA Status">
      <div className="space-y-2">
        <AccordionSection
          title="SLA Status"
          icon={<TimerIcon className="h-3.5 w-3.5" />}
          ruleName={conversation.slaPolicyName ?? null}
          count={slaCount}
        >
          {slaTimers.filter((t) => t.dueAt).map((t) => (
            <TimerCard
              key={t.label}
              label={t.label}
              dueAt={t.dueAt}
              startedAt={startedAt}
              completedAt={t.completedAt}
              targetMinutes={t.targetMinutes}
              timer={t.timer}
            />
          ))}
        </AccordionSection>

        <AccordionSection
          title="OLA Status"
          icon={<Network className="h-3.5 w-3.5" />}
          ruleName={conversation.olaPolicyName ?? null}
          count={olaCount}
          defaultOpen={olaCount > 0}
        >
          {olaTimers.map((t) => (
            <TimerCard
              key={t.label}
              label={t.label}
              dueAt={t.dueAt}
              startedAt={startedAt}
              completedAt={t.completedAt}
              targetMinutes={t.targetMinutes}
              timer={t.timer}
            />
          ))}
        </AccordionSection>
      </div>
    </Section>
  )
}
